// components/popup/RecoveryCourtPopup.tsx
import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { updateAssetReferral } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";

interface RecoveryCourtPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  existingData?: {
    asset_recovery_court_name?: string | null;
    asset_recovery_court_level?: string | null;
    asset_recovery_court_region?: string | null;
    asset_recovery_court_zone?: string | null;
    asset_recovery_court_woreda?: string | null;
    asset_recovery_first_hearing_date?: string | null;
    asset_recovery_request_court_registration_number?: string | null;
  };
  onSuccess?: () => void;
}

export const RecoveryCourtPopup: React.FC<RecoveryCourtPopupProps> = ({
  isOpen,
  onClose,
  referralId,
  existingData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    asset_recovery_court_name: "",
    asset_recovery_court_level: "",
    asset_recovery_court_region: "",
    asset_recovery_court_zone: "",
    asset_recovery_court_woreda: "",
    asset_recovery_first_hearing_date: "",
    asset_recovery_request_court_registration_number: "",
  });

  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();

  // Pre-fill form with existing data
  useEffect(() => {
    if (existingData && isOpen) {
      setFormData({
        asset_recovery_court_name: existingData.asset_recovery_court_name || "",
        asset_recovery_court_level:
          existingData.asset_recovery_court_level || "",
        asset_recovery_court_region:
          existingData.asset_recovery_court_region || "",
        asset_recovery_court_zone: existingData.asset_recovery_court_zone || "",
        asset_recovery_court_woreda:
          existingData.asset_recovery_court_woreda || "",
        asset_recovery_first_hearing_date:
          existingData.asset_recovery_first_hearing_date || "",
        asset_recovery_request_court_registration_number:
          existingData.asset_recovery_request_court_registration_number || "",
      });
    }
  }, [existingData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add this validation function inside the RecoveryCourtPopup component
  const validateForm = () => {
    if (!formData.asset_recovery_request_court_registration_number)
      return "Court Registration Number is required.";
    if (!formData.asset_recovery_court_name) return "Court Name is required.";
    if (!formData.asset_recovery_court_level) return "Court Level is required.";
    if (!formData.asset_recovery_first_hearing_date)
      return "First Hearing Date is required.";
    //   if (!formData.asset_recovery_court_region)
    //     return "Court Region is required.";
    //   if (!formData.asset_recovery_court_zone)
    //     return "Court Zone is required.";
    //   if (!formData.asset_recovery_court_woreda)
    //     return "Court Woreda is required.";
    return "";
  };

  // Update the handleSubmit function to include validation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for the update
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof typeof formData]) {
          submitFormData.append(key, formData[key as keyof typeof formData]);
        }
      });
      submitFormData.append("forward_status", "Forwarded to Coordinator");
      // Call API to update referral
      await updateAssetReferral(referralId, submitFormData);

      // Show success message
      showTooltip("Recovery court details updated successfully!", "success");

      // Reset form and close popup
      handleClose();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error updating prohibition court details:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update prohibition court details";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      asset_recovery_court_name: "",
      asset_recovery_court_level: "",
      asset_recovery_court_region: "",
      asset_recovery_court_zone: "",
      asset_recovery_court_woreda: "",
      asset_recovery_first_hearing_date: "",
      asset_recovery_request_court_registration_number: "",
    });
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Recovery Court Details"
      description="Add prohibition court information"
    >
      <form onSubmit={handleSubmit}>
        {/* Court Registration Number */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Court Registration Number
          </label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="asset_recovery_request_court_registration_number"
            placeholder="Enter court registration number"
            value={formData.asset_recovery_request_court_registration_number}
          />
        </div>

        {/* Court Name */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Name</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="asset_recovery_court_name"
            placeholder="Enter court name"
            value={formData.asset_recovery_court_name}
          />
        </div>

        {/* Court Level */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Level</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="asset_recovery_court_level"
            value={formData.asset_recovery_court_level}
            placeholder="Enter court level"
          />
        </div>

        {/* First Hearing Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">First Hearing Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="asset_recovery_first_hearing_date"
            value={formData.asset_recovery_first_hearing_date}
          />
        </div>

        {/* Court Location - Region, Zone, Woreda */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Region</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="asset_recovery_court_region"
              placeholder="Region"
              value={formData.asset_recovery_court_region}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Zone</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="asset_recovery_court_zone"
              placeholder="Zone"
              value={formData.asset_recovery_court_zone}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Woreda</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="asset_recovery_court_woreda"
              placeholder="Woreda"
              value={formData.asset_recovery_court_woreda}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </BasePopup>
  );
};
